import React, { useState } from "react";
import { Compass, Check, X, BookOpen, Download, ArrowRight } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { SectionLayout, SectionIntro, Panel } from "@/components/SectionLayout";
import { GuideViewer } from "@/components/guide/GuideViewer";
import { guideUrl } from "@/lib/api";
import { useRitualProgress, TawafCounter, SaiCounter, RitualChecklist, ResetProgress } from "@/components/guide/RitualTools";


export const UmrahGuide = ({ data }) => {
  const { L, t } = useLang();
  const [viewerOpen, setViewerOpen] = useState(false);
  const { state, toggleTawaf, toggleSai, toggleCheck, reset } = useRitualProgress();
  if (!data) return null;

  return (
    <SectionLayout id="umrah" icon={Compass}>
      <SectionIntro>{L(data.intro)}</SectionIntro>

      {/* Digital Umrah Guide */}
      <h2 className="mb-3 font-display text-xl font-bold text-[#0A192F]">{t("guide.title")}</h2>
      <button
        onClick={() => setViewerOpen(true)}
        data-testid="guide-read-btn"
        className="group mb-3 flex w-full items-center gap-4 rounded-[1.5rem] bg-[#0A192F] p-5 text-left shadow-[0_18px_44px_-16px_rgba(10,25,47,0.7)] transition-all duration-300 hover:-translate-y-0.5 active:scale-[0.99]"
      >
        <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-white/10 text-[#E9CE7D]">
          <BookOpen size={22} strokeWidth={1.7} />
        </span>
        <div className="min-w-0 flex-1">
          <span className="block font-display text-base font-bold text-white">{t("guide.read")}</span>
          <span className="mt-0.5 block text-xs text-white/60">{t("guide.readDesc")}</span>
        </div>
        <ArrowRight size={18} className="text-[#E9CE7D] transition-transform group-hover:translate-x-0.5" />
      </button>
      <a
        href={guideUrl("Maqam-Digital-Umrah-Guide.pdf")}
        download
        data-testid="guide-download-btn"
        className="group mb-8 flex w-full items-center gap-4 rounded-[1.5rem] border-2 border-slate-200 bg-white p-5 text-left transition-all duration-300 hover:-translate-y-0.5 hover:border-[#B9D0EC] active:scale-[0.99]"
      >
        <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-[#EAF2FC] text-[#3E6B9A]">
          <Download size={21} strokeWidth={1.7} />
        </span>
        <div className="min-w-0 flex-1">
          <span className="block font-display text-base font-bold text-[#0A192F]">{t("guide.download")}</span>
          <span className="mt-0.5 block text-xs text-slate-500">{t("guide.downloadDesc")}</span>
        </div>
      </a>

      {/* Interactive ritual tools */}
      <h2 className="font-display text-xl font-bold text-[#0A192F]">{t("guide.tools")}</h2>
      <p className="mb-4 mt-1 text-sm text-slate-500">{t("guide.toolsDesc")}</p>
      <div className="space-y-4">
        <TawafCounter state={state} onToggle={toggleTawaf} />
        <SaiCounter state={state} onToggle={toggleSai} />
        <RitualChecklist state={state} onToggle={toggleCheck} />
        <div className="flex justify-center pt-1">
          <ResetProgress onReset={reset} />
        </div>
      </div>

      {/* Quick reference (existing) */}
      <h2 className="mb-3 mt-10 font-display text-xl font-bold text-[#0A192F]">{t("umrah.steps")}</h2>
      <div className="space-y-3">
        {(data.steps || []).map((s) => (
          <Panel key={s.id} data-testid={`umrah-step-${s.id}`}>
            <h3 className="font-display text-lg font-medium text-[#0A192F]">{L(s.title)}</h3>
            <p className="mt-1 text-sm font-light leading-relaxed text-[#0A1128]/75">{L(s.body)}</p>
          </Panel>
        ))}
      </div>

      <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Panel className="border-[#2E7D32]/15">
          <h3 className="mb-2 flex items-center gap-2 font-display text-lg font-medium text-[#2E7D32]">
            <Check size={16} /> {t("umrah.dos")}
          </h3>
          <ul className="space-y-2 text-sm font-light text-[#0A1128]/75">
            {(data.dos || []).map((d, i) => (
              <li key={i}>• {L(d)}</li>
            ))}
          </ul>
        </Panel>
        <Panel className="border-[#C62828]/15">
          <h3 className="mb-2 flex items-center gap-2 font-display text-lg font-medium text-[#C62828]">
            <X size={16} /> {t("umrah.donts")}
          </h3>
          <ul className="space-y-2 text-sm font-light text-[#0A1128]/75">
            {(data.donts || []).map((d, i) => (
              <li key={i}>• {L(d)}</li>
            ))}
          </ul>
        </Panel>
      </div>

      {data.duaGuide && (
        <>
          <h2 className="mb-3 mt-8 font-display text-xl font-bold text-[#0A192F]">{t("umrah.dua")}</h2>
          <Panel className="bg-gradient-to-br from-[#F1F5F9] to-white">
            <div className="flex items-start gap-3">
              <BookOpen size={20} className="mt-0.5 shrink-0 text-[#D4AF37]" />
              <p className="text-sm font-light leading-relaxed text-[#0A1128]/75">{L(data.duaGuide)}</p>
            </div>
          </Panel>
        </>
      )}

      <GuideViewer open={viewerOpen} onClose={() => setViewerOpen(false)} />
    </SectionLayout>
  );
};
